import { useCallback, useContext, useState, useRef, useEffect } from 'react';
import { RefreshContext } from '../../../contexts/refresh-context';
import {
  ButtonContainer,
  FileSize,
  LinkButtonStyled,
  toastMessages,
  UploadButton,
  UploadButtonContainer,
  UploadContainerStyled,
  UploadImageStyled,
} from '../index.sc';
import {
  allowedFileTypes,
  convertExtensionToLowercase,
  CustomUploadFileInfo,
  isPasswordProtected,
  getPasswordErrorMessage,
} from '../../../utils/upload-helpers';
import UploadImage from '../../../images/cloud-upload-new.svg';
import exclamation from '../../../images/exclamation.png';
import {
  Upload,
  UploadFileInfo,
  UploadOnAddEvent,
  UploadOnRemoveEvent,
} from '@progress/kendo-react-upload';
import { formatBytes, prefixObjectValues } from '../../../utils';
import { PersonDataContext } from '../../../contexts/person-data-context';
import {
  Sortable,
  SortableItemUIProps,
  SortableOnDragOverEvent,
  SortableOnNavigateEvent,
} from '@progress/kendo-react-sortable';
import fileIcon from '../../../images/document.svg';
import { useMutation } from '@tanstack/react-query';
import { uploadAction2 } from '../api';
import {
  CircularGauge,
  CircularGaugeProps,
} from '@progress/kendo-react-gauges';
import NotificationSymbol from '../../../images/x-icon-red.svg';
import {
  NotificationBanner,
  NotificationBannerText,
} from '../../base/tw-components';
import VBClaimsModal from '../vb-claims-modal';
import { pushAnalyticsDataLayer } from '../../../utils/analytics/helpers';

const MAX_FILE_SIZE = 21000000;

const validationErrorMessages: Record<string, string> = {
  invalidFileExtension: 'This file type is not supported',
  invalidMaxFileSize: 'File exceeds the maximum size limit',
  invalidMinFileSize: 'File is too small',
};

interface UploadProps {
  keyId?: string;
  files?: CustomUploadFileInfo[];
  uploadTried?: boolean;
  selectedCase: string;
  docType: string;
  hasVbClaims: boolean;
  selectedVbClaimIds: string[];
  setSuccess: (success: boolean) => void;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDropDowns: React.Dispatch<React.SetStateAction<boolean>>;
}

interface AnalyticsData {
  name?: string;
  extension?: string;
  eid?: string;
  size?: string;
  errors?: string[];
  id?: number;
}

interface OnAddFilesParams {
  e: UploadOnAddEvent;
  files: UploadFileInfo[];
  setFiles: Function;
  handleDisplayToast: Function;
  setInsideDropZone: Function;
  setPasswordErrors: Function;
  eid?: string;
  setTotalFilesSize: Function;
  uploadContainerRef: React.RefObject<HTMLDivElement>;
}

const domIdsStatic = {
  rootNode: 'upload',
  dropzone: 'dropzone',
};

const DropzoneContentHint = ({
  onChooseFiles,
}: {
  onChooseFiles: () => void;
}) => (
  <>
    <UploadImageStyled>
      <img src={UploadImage} alt="upload panel" />
    </UploadImageStyled>
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <LinkButtonStyled
        className="btn btn-link"
        onClick={onChooseFiles}
        style={{
          lineHeight: '1.2',
          marginLeft: '2px',
          verticalAlign: 'baseline',
          backgroundColor: '#037CB7',
          color: '#fff',
          display: 'flex',
          padding: '8px, 12px',
        }}
      >
        Choose Files
      </LinkButtonStyled>
    </div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        color: '#037CB7',
        fontSize: '18px',
        fontWeight: 600,
        lineHeight: '1.55',
        paddingTop: '10px',
        paddingBottom: '24px',
      }}
    >
      Or drag and drop
    </div>
  </>
);

const DropzoneContentNote = (
  <>
    <div
      style={{
        color: '#999999',
        fontFamily: 'Open Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '1.5',
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '24px',
      }}
    >
      Allowed file types : pdf, jpg, jpeg, doc, docx, png, gif, rtf, txt, bmp
    </div>
    <div
      style={{
        color: '#999999',
        fontFamily: 'Open Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '1.5',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      Files cannot exceed {Math.floor(MAX_FILE_SIZE / 1000 / 1000)} MB
    </div>
  </>
);

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

const onAddFiles = async ({
  e,
  files,
  setFiles,
  handleDisplayToast,
  setInsideDropZone,
  setPasswordErrors,
  eid,
  setTotalFilesSize,
  uploadContainerRef,
}: OnAddFilesParams) => {
  setInsideDropZone(false);
  const newList = [...files];

  let errors = false;
  const fileData: AnalyticsData = {};

  for (const f of e.affectedFiles) {
    f.extension = f.extension?.toLowerCase();
    f.name = convertExtensionToLowercase(f.name); //convert ext and fileName ext to lowercase due to S3 upload errors
    const size = f.size ? f.size : 0;
    if ((f.validationErrors && f.validationErrors.length > 0) || size <= 0) {
      errors = true;
      fileData.name = f.name;
      fileData.size = formatBytes(f.size, 2);
      fileData.errors = f.validationErrors;
      fileData.extension = f.extension;
      fileData.eid = eid;
      newList.push(f);
    } else {
      // Check if the file is password protected
      if (f.getRawFile?.()) {
        const isProtected = await isPasswordProtected(f.getRawFile());
        if (isProtected) {
          setPasswordErrors((prev: string[]) => [...prev, f.name]);
        }
      }
      newList.push(f);
    }
  }

  setTotalFilesSize(newList.reduce((acc, file) => acc + (file.size ?? 0), 0));
  setFiles(newList);
  const hostEl = uploadContainerRef.current;

  if (errors) {
    const propsErrorDataGtmDl = {
      attribute1: 'upload a document',
      attribute2: 'undefined',
      attribute3: 'Choose document error',
      attribute4: fileData.errors,
      label: 'choose files error',
    };
    pushAnalyticsDataLayer(hostEl, propsErrorDataGtmDl, 'fileSelection');
    handleDisplayToast(toastMessages.validationError, 'error');
  }

  // analytics ////////////////////////////////////////////////

  const propsDataGtmDl = {
    attribute1: 'upload a document',
    attribute2: 'undefined',
    attribute3: 'pre-load_document',
    attribute4: 'undefined',
    label: 'Choose files pre-upload',
    category: 'button',
    action: 'click',
  };
  pushAnalyticsDataLayer(hostEl, propsDataGtmDl, 'fileSelection');
};

//sets the style of each sortable item
const getBaseItemStyle = (isActive: boolean) => ({
  height: 70,
  lineHeight: '68px',
  fontSize: '16px',
  textAlign: 'center',
  margin: '10px',
  fontWeight: 'bold',
  color: '#037CB7',
  outline: 'none',
  border: '1px solid',
  cursor: 'move',
  display: 'flex',
  shadow: '0 4px 8px 0 rgb(0 0 0 / 20%)',
  background: '#F6F8FA',
  borderColor: isActive ? '#037CB7' : '#fff',
  borderRadius: '8px',
});

interface SortableItemUIExtendedProps extends SortableItemUIProps {
  onRemove: (name: string) => void;
}

const SortableItemUI = (props: SortableItemUIExtendedProps) => {
  const { isActive, style, attributes, dataItem, forwardRef, onRemove } = props;
  const classNames = ['grid-cols-1 tw-mx-0'];
  const hasErrors =
    dataItem.validationErrors && dataItem.validationErrors.length > 0;

  return (
    <div
      ref={forwardRef}
      {...attributes}
      style={{
        ...getBaseItemStyle(isActive),
        ...style,
        ...(hasErrors ? { borderColor: '#dc2626', border: '2px solid' } : {}),
      }}
      className={classNames.join(' ')}
    >
      <img alt="file" className="pt-3 pb-3 pl-3" src={fileIcon} />
      <div id={dataItem.name} className="ml-2 text-clip overflow-hidden ...">
        {dataItem.name}
      </div>
      <FileSize color="#525252">{formatBytes(dataItem.size)}</FileSize>
      <ButtonContainer>
        <LinkButtonStyled
          onClick={() => onRemove(dataItem.name)}
          id={'deleteButton'}
          className="btn btn-link"
          style={{
            fontSize: '14px',
            verticalAlign: 'baseline',
          }}
        >
          Remove
        </LinkButtonStyled>
      </ButtonContainer>
    </div>
  );
};

const subbmittingMessage = (
  currentStep: any,
  sortableFiles: any,
  currentStepIndex: any,
) => {
  const arcOptions: CircularGaugeProps = {
    value: currentStepIndex,
    color: '#037CB7',
    scale: {
      max: sortableFiles.length + 1,
    },
  };
  const centerRenderer = () => {
    return (
      <span className="tw-font-bold tw-text-base">
        {currentStepIndex} of {sortableFiles.length + 1}
      </span>
    );
  };
  return (
    <>
      <div
        style={{
          alignContent: 'center',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: '48px',
        }}
      >
        <CircularGauge
          style={{ width: '100px', height: '100px' }}
          {...arcOptions}
          centerRender={centerRenderer}
        />
      </div>
      <div
        style={{
          fontFamily: 'Ubuntu',
          fontWeight: 400,
          fontSize: '26px',
          alignContent: 'center',
          textAlign: 'center',
        }}
      >
        <div>Submitting documents. </div>
        {currentStep && (
          <div className="text-clip overflow-hidden ...">
            {currentStep.toString()}
          </div>
        )}
      </div>
    </>
  );
};

const FileSelection: (p: UploadProps) => React.ReactElement = (p) => {
  const domIds = domIdsUnique(p.keyId);
  const [files, setFiles] = useState<Array<UploadFileInfo>>([]);
  const [insideDropZone, setInsideDropZone] = useState(false);
  const [displayToast, setDisplayToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const { personData } = useContext(PersonDataContext);
  const { isRefreshing } = useContext(RefreshContext);
  const [submittingFiles, setSubmittingFiles] = useState(false);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [totalFilesSize, setTotalFilesSize] = useState<number>(0);
  const [showVbClaimsModal, setShowVbClaimsModal] = useState(false);

  const fileValidationErrors = files
    .filter((f) => f.validationErrors && f.validationErrors.length > 0)
    .filter((f) => !passwordErrors.includes(f.name))
    .map((f) => ({
      name: f.name,
      errors: f.validationErrors!.filter((e) => e !== 'invalidMaxFileSize'),
    }))
    .filter((f) => f.errors.length > 0);

  const notificationBannerRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hasErrors =
      fileValidationErrors.length > 0 ||
      passwordErrors.length > 0 ||
      totalFilesSize >= 21000000;
    if (hasErrors && uploadButtonRef.current) {
      uploadButtonRef.current.style.scrollMarginBottom = '50px';
      uploadButtonRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [fileValidationErrors, passwordErrors, totalFilesSize]);

  //Maps the files to an array of objects with an id property to allow for sorting
  const sortableFiles = files.map((file, index) => {
    return {
      id: index,
      ...file,
    };
  });

  const removeFile = useCallback(
    (name: string) => {
      const fileIndex = files.findIndex((file) => file.name === name);
      const newFiles = [...files];
      newFiles.splice(fileIndex, 1);
      setFiles(newFiles);
      setTotalFilesSize(
        newFiles.reduce((acc, file) => acc + (file.size ?? 0), 0),
      );
      setPasswordErrors((prev) => {
        const index = prev.indexOf(name);
        if (index === -1) return prev;
        const copy = [...prev];
        copy.splice(index, 1);
        return copy;
      });
      //update the id of each file after removing one to prevent duplicate ids
      for (let i = 0; i < sortableFiles.length; i++) {
        sortableFiles[i].id = i;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, sortableFiles],
  );

  const sortableItemRenderer = useCallback(
    (itemProps: SortableItemUIProps) => (
      <SortableItemUI {...itemProps} onRemove={removeFile} />
    ),
    [removeFile],
  );

  const closeToast = () => {
    setDisplayToast(false);
  };

  setTimeout(() => {
    if (displayToast) {
      closeToast();
    }
  }, 8000);

  const handleDisplayToast = (message: string) => {
    setToastMessage(message);
    setDisplayToast(true);
  };

  const onRemoveFiles = (e: UploadOnRemoveEvent) => {
    setFiles(e.newState);
  };

  const onDragOver = (event: SortableOnDragOverEvent) => {
    setFiles(event.newState as CustomUploadFileInfo[]);
  };

  const onNavigate = (event: SortableOnNavigateEvent) => {
    setFiles(event.newState as CustomUploadFileInfo[]);
  };

  //kicks off the merge/upload process
  const mutateUpload = useMutation({
    mutationFn: () =>
      uploadAction2(
        p.selectedCase,
        p.docType,
        sortableFiles,
        setCurrentStep,
        setCurrentStepIndex,
        p.selectedVbClaimIds,
      ),
    onSuccess: (data) => {
      p.setSuccess(true);
      console.log('Mutation success', data);
    },
    onError: (data) => {
      p.setError(true);
      console.log('Mutation error', data);
    },
  });

  const startUpload = () => {
    setShowVbClaimsModal(false);
    mutateUpload.mutate();
    setSubmittingFiles(true);
    p.setShowDropDowns(false);
  };

  const onUploadClick = () => {
    if (p.hasVbClaims && p.selectedVbClaimIds.length === 0) {
      setShowVbClaimsModal(true);
      return;
    }
    startUpload();
  };

  const uploadContainerRef = useRef<HTMLDivElement>(null);
  const eid = personData?.person?.eid;

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setInsideDropZone(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length === 0) return;
      const affectedFiles = droppedFiles.map((file, idx) => {
        const extNoDot = file.name.includes('.')
          ? file.name.split('.').pop()!.toLowerCase()
          : '';
        const ext = extNoDot ? `.${extNoDot}` : '';
        const validationErrors: string[] = [];
        if (!allowedFileTypes.includes(extNoDot)) {
          validationErrors.push('invalidFileExtension');
        }
        if (file.size > MAX_FILE_SIZE) {
          validationErrors.push('invalidMaxFileSize');
        }
        return {
          name: file.name,
          extension: ext,
          size: file.size,
          uid: `${Date.now()}-${idx}`,
          getRawFile: () => file,
          status: 0,
          progress: 0,
          validationErrors,
        };
      });
      onAddFiles({
        e: { affectedFiles } as any,
        files,
        setFiles,
        handleDisplayToast,
        setInsideDropZone,
        setPasswordErrors,
        eid,
        setTotalFilesSize,
        uploadContainerRef,
      });
    },
    [files, eid],
  );

  if (submittingFiles === false) {
    return (
      <div id={'fileSelection'}>
        <UploadContainerStyled
          className={insideDropZone ? 'dropzone-entered' : ''}
          onDragOver={(e) => {
            e.preventDefault();
            setInsideDropZone(true);
          }}
          onDragLeave={() => {
            setInsideDropZone(false);
          }}
          onDrop={handleDrop}
          ref={uploadContainerRef}
          data-gtm-dl={JSON.stringify({
            attribute1: 'upload a document',
            attribute2: 'undefined',
            attribute3: 'choose_upload_files',
            attribute4: 'undefined',
            category: 'button',
            label: 'choose files',
          })}
        >
          <Upload
            showActionButtons={false}
            showFileList={false}
            id={domIds.dropzone}
            autoUpload={false}
            files={files}
            onAdd={(e) =>
              onAddFiles({
                e,
                files,
                setFiles,
                handleDisplayToast,
                setInsideDropZone,
                setPasswordErrors,
                eid,
                setTotalFilesSize,
                uploadContainerRef,
              })
            }
            onRemove={onRemoveFiles}
            multiple={true}
            batch={false}
            withCredentials={false}
            restrictions={{
              maxFileSize: MAX_FILE_SIZE,
              allowedExtensions: allowedFileTypes,
            }}
          />
          <DropzoneContentHint
            onChooseFiles={() => {
              const fileInput = uploadContainerRef.current?.querySelector(
                'input[type="file"]',
              ) as HTMLInputElement;
              fileInput?.click();
            }}
          />
          {DropzoneContentNote}
        </UploadContainerStyled>
        {displayToast && (
          <div className="tw-flex tw-items-center tw-bg-unm-denied-bg tw-rounded-lg tw-p-3 tw-pl-4 tw-mt-4 tw-mb-4 tw-w-full">
            <img
              alt="Info circle"
              src={NotificationSymbol}
              className="tw-self-center"
            />
            <NotificationBannerText key="success">
              {toastMessage}
            </NotificationBannerText>
          </div>
        )}
        {sortableFiles.length > 0 ? (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Sortable
              idField={'id'}
              disabledField={'disabled'}
              data={sortableFiles}
              itemUI={sortableItemRenderer}
              onDragOver={onDragOver}
              onNavigate={onNavigate}
            />

            {(fileValidationErrors.length > 0 ||
              passwordErrors.length > 0 ||
              totalFilesSize >= 21000000) && (
              <div ref={notificationBannerRef}>
                <NotificationBanner>
                  <img
                    alt="info"
                    src={exclamation}
                    className="tw-self-center tw-scale-[0.6]"
                  />
                  <NotificationBannerText
                    key="errors"
                    textColorClass="tw-text-unm-error-red"
                  >
                    {fileValidationErrors.map((f) => (
                      <div key={f.name} className="tw-mb-1">
                        {f.errors
                          .map((e) => validationErrorMessages[e] ?? e)
                          .join(', ')}{' '}
                        - {f.name}
                      </div>
                    ))}
                    {passwordErrors.length > 0 && (
                      <div className="tw-mb-1">
                        {getPasswordErrorMessage(passwordErrors)}
                      </div>
                    )}
                    {totalFilesSize >= 21000000 && (
                      <div>Your total file size exceeds the 21 MB limit.</div>
                    )}
                  </NotificationBannerText>
                </NotificationBanner>
              </div>
            )}
            <div className="tw-flex tw-justify-end tw-font-sans tw-font-semibold tw-text-sm tw-leading-20 tw-align-middle tw-text-color-unm-adhoc2 tw-mb-2">
              Total amount: {formatBytes(totalFilesSize)} | Remaining:{' '}
              {formatBytes(Math.max(MAX_FILE_SIZE - totalFilesSize, 0))}
            </div>
            <UploadButtonContainer
              ref={uploadButtonRef}
              className="mr-5 d-flex justify-content-end align-items-center"
            >
              <UploadButton
                onClick={onUploadClick}
                id={domIds.uploadButton}
                disabled={
                  fileValidationErrors.length > 0 ||
                  passwordErrors.length > 0 ||
                  totalFilesSize >= 21000000 ||
                  isRefreshing
                }
                data-gtm-dl={JSON.stringify({
                  attribute1: 'upload a document',
                  attribute2: 'undefined',
                  attribute3: 'attempt_upload_document',
                  attribute4: 'undefined',
                  category: 'button',
                  label: 'submit',
                })}
              >
                Upload documents
              </UploadButton>
            </UploadButtonContainer>
          </div>
        ) : null}
        {showVbClaimsModal && (
          <VBClaimsModal
            onGoBack={() => setShowVbClaimsModal(false)}
            onContinue={startUpload}
          />
        )}
      </div>
    );
  } else {
    return subbmittingMessage(currentStep, sortableFiles, currentStepIndex);
  }
};
export default FileSelection;
